import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ChevronRight, Search, X, MessageCircle } from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';

export const FAQPage = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useLangPrefix();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const CATEGORIES = [
    { key: 'all', label: t('faq.cat.all') },
    { key: 'general', label: t('faq.cat.general') },
    { key: 'formations', label: t('faq.cat.formations') },
    { key: 'financement', label: t('faq.cat.financement') },
    { key: 'inscription', label: t('faq.cat.inscription') },
    { key: 'pratique', label: t('faq.cat.pratique') },
    { key: 'apres', label: t('faq.cat.apres') },
  ];
  const CATEGORY_LABELS = CATEGORIES.reduce((m, c) => ({ ...m, [c.key]: c.label }), {});

  useEffect(() => {
    api.listFaq({ lang }).then(setItems).finally(() => setLoading(false));
  }, [lang, i18n.language]);

  const grouped = useMemo(() => {
    const filtered = items.filter(item => {
      if (activeCategory !== 'all' && item.category !== activeCategory) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return item.question.toLowerCase().includes(q) || item.answer.toLowerCase().includes(q);
    });
    const groups = {};
    filtered.forEach(item => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [items, search, activeCategory]);

  const totalFiltered = Object.values(grouped).reduce((acc, arr) => acc + arr.length, 0);

  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A] min-h-screen">
      {/* Hero — compact */}
      <section className="pt-32 pb-4 md:pt-40 md:pb-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-3">
            <Link to={withLangPrefix('/', lang)} className="hover:text-[#C9B896] transition-colors">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A]">{t('nav.faq')}</span>
          </div>
          <h1 className="font-light text-[#1F2A4A] leading-[1.05] tracking-tight text-2xl md:text-4xl max-w-4xl" data-testid="faq-title">
            <span>{t('faq.title_line1')} </span>
            <span className="font-serif italic text-[#C9B896]">{t('faq.title_line2')}</span>
          </h1>
          <p className="text-[#4A5568] text-sm font-light max-w-2xl mt-2 leading-relaxed">
            {t('faq.subtitle')}
          </p>
        </div>
      </section>

      {/* Search + filters */}
      <section className="py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C9B896]" strokeWidth={1.5} />
            <Input
              placeholder={t('faq.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none pl-7 pr-8 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] font-light text-sm h-10"
              data-testid="faq-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-0 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#1F2A4A]/5 flex items-center justify-center">
                <X className="w-3.5 h-3.5 text-[#1F2A4A]/60" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            {CATEGORIES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                data-testid={`faq-cat-${key}`}
                className={`pb-1 border-b transition-all duration-300 ${
                  activeCategory === key
                    ? 'text-[#1F2A4A] font-medium border-[#C9B896]'
                    : 'text-[#4A5568]/70 hover:text-[#1F2A4A] border-transparent hover:border-[#C9B896]/40'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ list */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-16 bg-white border border-[#1F2A4A]/8 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : totalFiltered === 0 ? (
            <div className="text-center py-20">
              <h3 className="font-light text-3xl md:text-4xl text-[#1F2A4A] mb-3">
                {t('faq.no_results_title')}
              </h3>
              <p className="text-[#4A5568] mb-8 max-w-md mx-auto">
                {t('faq.no_results_subtitle')}
              </p>
              <Link to={withLangPrefix('/contact', lang)}>
                <Button className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-9 py-7 text-sm uppercase tracking-[0.25em] font-medium">
                  {t('cta.contact_us')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-14">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <div className="flex items-baseline gap-5 mb-6">
                    <span className="h-px flex-1 bg-[#1F2A4A]/12" />
                    <span className="text-xs text-[#4A5568]">{list.length} question{list.length > 1 ? 's' : ''}</span>
                  </div>
                  <Accordion type="single" collapsible className="space-y-3">
                    {list.map((item) => (
                      <AccordionItem
                        key={item.id}
                        value={item.id}
                        className="bg-white border border-[#1F2A4A]/8 hover:border-[#C9B896]/50 rounded-xl px-6 transition-colors data-[state=open]:border-[#C9B896] data-[state=open]:shadow-sm"
                        data-testid={`faq-item-${item.id}`}
                      >
                        <AccordionTrigger className="text-left text-base md:text-lg font-medium text-[#1F2A4A] py-5 hover:no-underline hover:text-[#C9B896]">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-[#4A5568] font-light leading-relaxed text-base pb-5">
                          {item.answer.split('\n').map((line, i) => (
                            <p key={i} className="mb-3 last:mb-0">{line}</p>
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#C9B896]/15 mb-6">
            <MessageCircle className="w-6 h-6 text-[#C9B896]" />
          </div>
          <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A] mb-4">
            {t('faq.contact_cta_kicker')} <span className="font-serif italic">{t('faq.contact_cta_em')}</span>
          </h2>
          <p className="text-[#4A5568] text-base font-light max-w-xl mx-auto leading-relaxed mb-8">
            {t('faq.contact_cta_subtitle')}
          </p>
          <Link to={withLangPrefix('/contact', lang)}>
            <Button variant="outline" className="border-[#1F2A4A]/20 text-[#1F2A4A] hover:bg-[#1F2A4A] hover:text-[#FAF7F0] rounded-full px-9 py-7 text-sm uppercase tracking-[0.25em] font-medium" data-testid="faq-contact-cta">
              {t('faq.contact_cta_button')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
